
import { db, functions } from '../firebase';
import { collection, query, where, getDocs, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { IntegrationProviderInfo } from '../types';
import { FirestoreUtils } from './FirestoreUtils';

export interface ProjectMapping {
    id: string;
    provider: string;
    projectId: string;
    label: string;
}

export const IntegrationService = {
    /**
     * Subscribes to external identities for an organization.
     * MOVED FROM StandUpContext.tsx (External Identities Listener)
     */
    listenToIdentities(orgId: string, callback: (idMap: Record<string, any>) => void) {
        const q = query(collection(db, 'externalIdentities'), where('orgId', '==', orgId));
        return onSnapshot(q, (snapshot) => {
            const idMap: Record<string, any> = {};
            snapshot.docs.forEach(doc => {
                idMap[doc.id] = doc.data();
            });
            callback(idMap);
        });
    },

    /**
     * Subscribes to all enabled global providers.
     * MOVED FROM StandUpContext.tsx (Providers Listener)
     */
    listenToProviders(callback: (providers: IntegrationProviderInfo[]) => void) {
        const q = query(collection(db, 'providers'), where('enabled', '==', true));
        return onSnapshot(q, (snapshot) => {
            callback(snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as IntegrationProviderInfo)));
        });
    },

    /**
     * Subscribes to project team mapping for active teams.
     * Refactored: Added orgId scope and chunking for scalability.
     */
    listenToProjectTeamMap(orgId: string, teamIds: string[], callback: (map: Record<string, string>) => void) {
        if (!teamIds || teamIds.length === 0) {
            callback({});
            return () => {};
        }

        const baseQuery = query(collection(db, 'projectTeamMap'), where('orgId', '==', orgId));
        
        return FirestoreUtils.onSnapshotChunked(baseQuery, 'standUpTeamId', teamIds, (results) => {
            const map: Record<string, string> = {};
            results.forEach(data => {
                const key = `${data.provider}_${data.projectId}`;
                map[key] = data.standUpTeamId;
            });
            callback(map);
        });
    },

    /**
     * Fetches all project mappings for a specific StandUp team.
     * Maps firestore 'projectTeamMap' docs to ProjectMapping objects.
     */
    async fetchTeamProjectMappings(teamId: string): Promise<ProjectMapping[]> {
        const q = query(collection(db, 'projectTeamMap'), where('standUpTeamId', '==', teamId));
        const snap = await getDocs(q);
        
        return snap.docs.map(doc => {
            const data = doc.data();
            return {
                id: `${data.provider}_${data.projectId}`,
                provider: data.provider,
                projectId: data.projectId,
                label: `${data.provider.toUpperCase()}: ${data.projectId}`
            };
        });
    },

    /**
     * Fetches the list of external projects from a specific provider.
     * Calls the 'getExternalProjectList' cloud function.
     */
    async getExternalProjectList(provider: string): Promise<any[]> {
        const getList = httpsCallable(functions, 'getExternalProjectList');
        try {
            const res: any = await getList({ provider });
            if (res.data?.success) {
                return res.data.projects || [];
            }
            return [];
        } catch (e) {
            console.error(`[IntegrationService] Failed to fetch project list for ${provider}`, e);
            return [];
        }
    },

    /**
     * Fetches all project mappings for a list of team IDs.
     * Refactored: Added orgId scope and chunking for scalability.
     */
    async fetchProjectMappingsForTeams(orgId: string, teamIds: string[]): Promise<any[]> {
        if (!teamIds || teamIds.length === 0) return [];

        const baseQuery = query(collection(db, 'projectTeamMap'), where('orgId', '==', orgId));
        return await FirestoreUtils.getDocsChunked(baseQuery, 'standUpTeamId', teamIds);
    },

    /**
     * Manually triggers the full sync cloud function.
     */
    async syncExternalTasks() {
        console.log(`[Integration] Manually triggering full sync...`);
        try {
            const syncFunc = httpsCallable(functions, 'syncExternalTasks');
            await syncFunc();
            console.log(`[Integration] Manual sync request completed`);
        } catch (e: any) {
            console.error("[Integration Error] Manual sync failed:", e);
            throw e;
        }
    },

    /**
     * Updates integration sync preferences for a specific provider.
     */
    async updateSyncPreference(userId: string, provider: string, updatedKeys: string[]) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            [`integrations.${provider}`]: {
                enabled: true,
                disabledProjectKeys: updatedKeys
            }
        });
    },

    /**
     * Triggers the cloud function to create an external task.
     */
    async createExternalTask(data: any) {
        console.log(`[Integration] Creating external task: "${data.title}" in ${data.projectKey}`);
        try {
            const createFunc = httpsCallable(functions, 'createExternalTask');
            await createFunc(data);
            console.log(`[Integration] External task created successfully`);
        } catch (e: any) {
            console.error("[Integration Error] Failed to create external task:", e);
            throw e;
        }
    },

    /**
     * Pushes a status/assignee update for an external task.
     */
    async updateExternalTaskStatus(payload: any, provider: string) {
        console.log(`[Integration] Pushing update to ${provider}. Payload:`, payload);
        try {
            const pushUpdate = httpsCallable(functions, 'updateExternalTaskStatus');
            await pushUpdate(payload);
            console.log(`[Integration] External update push successful`);
        } catch (e: any) {
            console.error("[Integration Error] External update failed:", e);
            throw e;
        }
    }
};
